import { StyledSpinner } from '@app/evaluation/components/Spinner'
import images from '@app/images'
import theme from '@app/ui/theme'
import React, { useEffect, useState } from 'react'

import { useLogin } from '../hooks'
import {
  LoginContainer,
  LoginLogo,
  LoginWrapper,
  ErrorText,
  ErrorTextContainer,
  BG,
  AttentionCard,
  AttentionTitle,
  AttentionText,
  PasswordInputWrapper,
  PasswordToggleButton,
} from './Login.styles'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import Spacer from '@app/components/atoms/Spacer'

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
)

const Login = ({ prefilledEmail }: { prefilledEmail?: string }) => {
  const { signIn, inputs, error, loading } = useLogin()
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (prefilledEmail) {
      inputs.setEmail(prefilledEmail)
    }
  }, [])

  return (
    <LoginContainer>
      <div
        style={{
          zIndex: 1,
          position: 'absolute',
          height: '100%',
          width: '100%',
          background: theme.colors.primary,
        }}
      >
        <BG />
      </div>
      <LoginWrapper>
        <LoginLogo src={images.svg.LiveRateIcon} style={{ height: 120 }} />
        <Spacer height={24} />
        <AttentionCard>
          <AttentionTitle>
            <span role="img" aria-label="warning">
              ⚠️
            </span>
            Attention!
          </AttentionTitle>
          <AttentionText>
            After 25 November 2025 all users with an account at MinGolf need to
            update their password. This is done at{' '}
            <a
              href="https://mingolf.golf.se/login"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://mingolf.golf.se/login
            </a>
            . This is required to login with your GolfID with password here in
            MiRate.
          </AttentionText>
        </AttentionCard>
        <Spacer height={16} />
        <form
          style={{ width: '100%', maxWidth: 500 }}
          onSubmit={(event) => {
            event.preventDefault()
            signIn()
          }}
        >
          <input
            type="text"
            name="username"
            placeholder="Email-adress or GolfID"
            value={inputs.email}
            onChange={(event) => inputs.setEmail(event.target.value)}
            className="login-username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <PasswordInputWrapper>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Password"
              value={inputs.password}
              onChange={(event) => inputs.setPassword(event.target.value)}
              className="login-password"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <PasswordToggleButton
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </PasswordToggleButton>
          </PasswordInputWrapper>
          <Spacer height={8} />
          <div style={{ paddingLeft: 8, paddingRight: 8 }}>
            <RoundedButton onPress={signIn} loading={loading} title="Sign In" />
          </div>
          <button
            type="submit"
            aria-hidden="true"
            tabIndex={-1}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              border: 0,
              opacity: 0,
              pointerEvents: 'none',
            }}
          />
        </form>
        {error && !loading && (
          <ErrorTextContainer>
            <ErrorText>{error}</ErrorText>
          </ErrorTextContainer>
        )}
      </LoginWrapper>
      <div
        style={{
          width: 200,
          height: 50,
          position: 'absolute',
          bottom: 8,
          left: 8,
          zIndex: 2,
        }}
      >
        <img
          src={images.png.MappingLogo}
          style={{
            height: '100%',
            width: '100%',
          }}
        />
      </div>
    </LoginContainer>
  )
}

export default Login
